import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { applicationApi } from '@/api/endpoints'
import { ApplicationStatusPill } from '@/components/ui/StatusPill'
import { Kbd } from '@/components/ui/Kbd'
import { setTheme, useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { useChatStore } from '@/store/chat'
import { fromCurrent } from '@/features/applications/backTarget'

/**
 * ⌘K: jump anywhere, find an application by company or role, flip the theme.
 * The whole app is reachable from the keyboard; on a phone the same palette
 * opens from the search button in the top bar.
 */
interface Command {
  id: string
  label: string
  hint?: string
  group: 'Go to' | 'Actions' | 'Applications'
  keys?: string[]
  run: () => void
  pill?: React.ReactNode
}

export function useCommandPalette() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((value) => !value)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  return { open, setOpen }
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const [, resolved] = useTheme()

  useEffect(() => {
    if (open) {
      setQuery('')
      setCursor(0)
      window.setTimeout(() => input.current?.focus(), 10)
    }
  }, [open])

  const search = query.trim()
  const applications = useQuery({
    queryKey: ['palette-applications', search],
    queryFn: () => applicationApi.list({ pageSize: 6, search }),
    enabled: open && search.length >= 2,
    staleTime: 15_000,
  })

  const go = (to: string) => () => {
    onClose()
    // An application opened from here goes back to the screen the palette was opened on.
    navigate(to, { state: fromCurrent() })
  }

  const commands = useMemo<Command[]>(() => {
    const base: Command[] = [
      { id: 'home', label: 'Home', hint: 'Dashboard', group: 'Go to', keys: ['g', 'h'], run: go('/dashboard') },
      { id: 'jobs', label: 'Browse jobs', group: 'Go to', keys: ['g', 'j'], run: go('/dashboard/jobs') },
      { id: 'auto', label: 'Auto apply', group: 'Go to', keys: ['g', 'a'], run: go('/dashboard/auto-apply') },
      { id: 'tracker', label: 'Tracking', hint: 'Inbox · Pipeline · Applications', group: 'Go to', keys: ['g', 't'], run: go('/dashboard/tracker') },
      { id: 'review', label: 'Needs review', hint: 'Every open question in one place', group: 'Go to', keys: ['g', 'r'], run: go('/dashboard/review') },
      { id: 'needs', label: 'Applications that need you', group: 'Go to', run: go('/dashboard/tracker?tab=applications&status=needs_you') },
      { id: 'profile', label: 'Profile', group: 'Go to', keys: ['g', 'p'], run: go('/dashboard/profile') },
      { id: 'portals', label: 'Job portals', group: 'Go to', run: go('/dashboard/profile/portals') },
      { id: 'settings', label: 'Settings', group: 'Go to', keys: ['g', 's'], run: go('/dashboard/settings') },
      { id: 'plans', label: 'Plans & billing', hint: 'Starter · Pro · Power', group: 'Go to', run: go('/dashboard/plans') },
      { id: 'subscription', label: 'My subscription', hint: 'Usage this month, billing, history', group: 'Go to', run: go('/dashboard/profile/subscription') },
      {
        id: 'theme',
        label: resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        group: 'Actions',
        run: () => {
          setTheme(resolved === 'dark' ? 'light' : 'dark')
          onClose()
        },
      },
      { id: 'theme-system', label: 'Follow the system theme', group: 'Actions', run: () => { setTheme('system'); onClose() } },
      {
        id: 'ask',
        label: 'Ask the assistant',
        hint: 'Matches, applications, apply for you',
        group: 'Actions',
        keys: ['mod', 'J'],
        run: () => {
          onClose()
          useChatStore.getState().setOpen(true)
        },
      },
    ]
    const apps: Command[] = (applications.data?.data ?? []).map((application) => ({
      id: `app-${application.id}`,
      label: application.job.companyName,
      hint: application.job.title,
      group: 'Applications',
      pill: <ApplicationStatusPill status={application.status} readyToSubmit={application.readyToSubmit} needsReview={application.needsReview} size="sm" />,
      run: go(`/dashboard/applications/${application.id}`),
    }))
    const words = search.toLowerCase().split(/\s+/).filter(Boolean)
    const matches = (command: Command) =>
      words.every((word) => `${command.label} ${command.hint ?? ''}`.toLowerCase().includes(word))
    return [...apps, ...base.filter((command) => (search ? matches(command) : true))]
  }, [applications.data, resolved, search]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setCursor(0), [search, commands.length])

  if (!open) return null

  const onKey = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((value) => Math.min(value + 1, commands.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((value) => Math.max(value - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      commands[cursor]?.run()
    } else if (event.key === 'Escape') {
      onClose()
    }
  }

  const groups = ['Applications', 'Go to', 'Actions'] as const
  let index = -1

  return (
    <div className="fixed inset-0 z-[60]" role="presentation">
      <div className="absolute inset-0 animate-fade-in bg-ink/40 backdrop-blur-[2px]" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="absolute inset-x-0 top-0 mx-auto w-full max-w-[640px] animate-pop-in overflow-hidden bg-surface shadow-float sm:top-[12vh] sm:rounded-card sm:border sm:border-line"
        style={{ paddingTop: 'var(--safe-top)' }}
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <SearchIcon />
          <input
            ref={input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKey}
            placeholder="Search applications, jump anywhere…"
            aria-label="Search"
            className="h-14 min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-faint"
          />
          <button type="button" onClick={onClose} className="rounded-field px-2 py-1 text-[12px] text-ink-muted hover:bg-surface-muted sm:hidden">
            Cancel
          </button>
          <Kbd keys="Esc" />
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-2">
          {commands.length === 0 && (
            <p className="px-4 py-8 text-center text-[14px] text-ink-muted">
              {applications.isFetching ? 'Searching…' : 'Nothing matches'}
            </p>
          )}
          {groups.map((group) => {
            const items = commands.filter((command) => command.group === group)
            if (items.length === 0) return null
            return (
              <div key={group} className="px-2 pb-1">
                <p className="label-eyebrow px-2 pb-1 pt-2">{group}</p>
                {items.map((command) => {
                  index += 1
                  const active = index === cursor
                  const position = index
                  return (
                    <button
                      key={command.id}
                      type="button"
                      onMouseEnter={() => setCursor(position)}
                      onClick={command.run}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-field px-3 py-2.5 text-left transition-colors',
                        active ? 'bg-accent-soft' : 'hover:bg-surface-muted',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-medium text-ink">{command.label}</span>
                        {command.hint && <span className="block truncate text-[12.5px] text-ink-muted">{command.hint}</span>}
                      </span>
                      {command.pill}
                      {command.keys && <Kbd keys={command.keys} />}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function SearchIcon() {
  return (
    <svg className="h-4 w-4 shrink-0 text-ink-muted" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="m10.5 10.5 3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
